import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../Button';
import { EntityPicker } from '../EntityPicker';
import { listIntentMatcherSkillCatalog } from '../../../services/api';

/**
 * IntentMatcherEditor — inline, catalog-aware editor for a version's allowed
 * skill matchers. Consolidated INTO the authoring console (no slideout).
 *
 * Controlled: {@code value} is the matcher array [{mode,value}], {@code onChange}
 * receives the new array. Three modes mirror the backend
 * (IntentTemplateVersion.MatcherMode):
 *   explicit-skill → searchable, grouped, paged EntityPicker (stores the skill UUID)
 *   skill-name     → free text + type-ahead of existing skill names
 *   skill-glob     → free text + live "matches N" preview against the catalog
 *
 * The skill catalog is loaded once and is already scoped server-side to the
 * caller's active tenant. explicit-skill uses the shared EntityPicker (the same
 * combobox the framework-registration surface uses) so a large catalog stays
 * searchable and grouped by agent instead of an OS-drawn list. skill-name and
 * skill-glob remain free text on purpose — the backend accepts a name or glob
 * that need not exist in the catalog yet (template-first authoring) — but browser
 * autofill is disabled so the field never shows the browser's own suggestions.
 *
 * If the catalog fails to load the editor degrades to a plain UUID text input
 * for explicit-skill (still fully usable).
 */
export function IntentMatcherEditor({ value, onChange, disabled }) {
  const matchers = Array.isArray(value) ? value : [];
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      const res = await listIntentMatcherSkillCatalog();
      if (!active) return;
      setLoading(false);
      const data = res?.data ?? res;
      setCatalog(Array.isArray(data) ? data : []);
    })();
    return () => { active = false; };
  }, []);

  const names = useMemo(() => {
    const seen = new Set(); const out = [];
    for (const s of catalog) { if (s?.name && !seen.has(s.name.toLowerCase())) { seen.add(s.name.toLowerCase()); out.push(s.name); } }
    return out;
  }, [catalog]);

  // Group the explicit-skill picker by owning agent, so a long catalog reads as
  // "which agent exposes this skill" rather than one flat wall of names. Tabs
  // only render when more than one agent is present (EntityPicker hides them
  // otherwise), so a single-agent tenant sees a plain searchable list.
  const agentGroups = useMemo(() => {
    const seen = new Map();
    for (const s of catalog) {
      const gid = s?.agentId ? String(s.agentId) : '__unassigned__';
      if (!seen.has(gid)) seen.set(gid, s?.agentName || 'Unassigned');
    }
    return Array.from(seen.entries()).map(([id, label]) => ({ id, label }));
  }, [catalog]);

  const update = (i, patch) => onChange(matchers.map((m, idx) => idx === i ? { ...m, ...patch } : m));
  const remove = (i) => onChange(matchers.filter((_, idx) => idx !== i));
  // Default a new matcher to explicit-skill so the author lands on the searchable
  // picker; they can switch to skill-name / skill-glob for pattern matching.
  const add = () => onChange([...matchers, { mode: 'explicit-skill', value: '' }]);

  return (
    <div>
      {loading && (
        <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginBottom: 6, display: 'flex', gap: 6, alignItems: 'center' }}>
          <Loader2 size={11} className="animate-spin" /> Loading skill catalog…
        </div>
      )}
      {matchers.length === 0 && (
        <div style={{
          padding: 12, marginBottom: 8, fontSize: 12.5, color: 'var(--v2-text-muted)',
          background: 'var(--v2-bg-muted)', border: '1px dashed var(--v2-border-strong)', borderRadius: 6,
        }}>
          No matchers yet. Add at least one before submitting for review.
        </div>
      )}
      <datalist id="intent-skill-names">
        {names.map((n) => <option key={n} value={n} />)}
      </datalist>
      {matchers.map((m, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'flex-start' }}>
          <select value={m.mode} disabled={disabled} onChange={(e) => update(i, { mode: e.target.value, value: '' })}
            style={{ ...field, width: 130, flexShrink: 0 }}>
            <option value="explicit-skill">explicit-skill</option>
            <option value="skill-name">skill-name</option>
            <option value="skill-glob">skill-glob</option>
          </select>
          <div style={{ flex: 1, minWidth: 0 }}>
            {m.mode === 'explicit-skill' && catalog.length > 0 ? (
              <EntityPicker
                items={catalog}
                value={m.value || null}
                onChange={(id) => update(i, { value: id || '' })}
                disabled={disabled}
                loading={loading}
                getId={(s) => s.id}
                getLabel={(s) => s.name}
                getSublabel={(s) => s.agentName || null}
                getSearchText={(s) => [s.name, s.agentName, s.id].filter(Boolean).join(' ')}
                groups={agentGroups.length > 1 ? agentGroups : null}
                getGroupId={(s) => (s?.agentId ? String(s.agentId) : '__unassigned__')}
                allTabLabel="All agents"
                ariaLabel="Select a skill"
                placeholder="Select a skill"
                searchPlaceholder="Search skills"
                loadingText="Loading skills…"
                emptyMessage={(q, total) => (q
                  ? `No skill matches “${q}”. Try fewer characters, or clear the search to see all ${total}.`
                  : 'No skills are available to match in this scope yet.')}
              />
            ) : m.mode === 'explicit-skill' ? (
              <input
                value={m.value} disabled={disabled}
                onChange={(e) => update(i, { value: e.target.value })}
                autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
                name={`intent-matcher-skill-${i}`}
                placeholder="skill UUID"
                style={{ ...field, width: '100%', fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}
              />
            ) : (
              <input
                value={m.value} disabled={disabled}
                onChange={(e) => update(i, { value: e.target.value })}
                list={m.mode === 'skill-name' ? 'intent-skill-names' : undefined}
                autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
                name={`intent-matcher-${m.mode}-${i}`}
                placeholder={m.mode === 'skill-glob' ? 'org.read.*' : 'send_email'}
                style={{ ...field, width: '100%', fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}
              />
            )}
            {m.mode === 'skill-glob' && m.value && (
              <GlobHint pattern={m.value} catalog={catalog} />
            )}
          </div>
          <button type="button" onClick={() => remove(i)} disabled={disabled} title="Remove matcher"
            style={{ height: 34, width: 34, flexShrink: 0, boxSizing: 'border-box',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--v2-bg-surface)', border: '1px solid var(--v2-border-strong)',
              borderRadius: 6, color: 'var(--v2-danger)', cursor: disabled ? 'not-allowed' : 'pointer' }}>
            <Trash2 size={13} />
          </button>
        </div>
      ))}
      <Button variant="secondary" size="sm" iconLeft={<Plus />} onClick={add} disabled={disabled}>Add matcher</Button>
    </div>
  );
}

function GlobHint({ pattern, catalog }) {
  const n = useMemo(() => {
    let re; try { re = globToRegex(pattern); } catch { return null; }
    const s = new Set();
    for (const k of catalog) if (k?.name && re.test(k.name)) s.add(k.name.toLowerCase());
    return s.size;
  }, [pattern, catalog]);
  if (n == null) return null;
  return (
    <div style={{ fontSize: 11, color: n === 0 ? 'var(--v2-warning)' : 'var(--v2-text-muted)', marginTop: 3 }}>
      Matches {n} skill{n === 1 ? '' : 's'} in your catalog now (resolved per-agent at bind).
    </div>
  );
}

function globToRegex(glob) {
  let re = '';
  for (const c of glob) {
    if (c === '*') re += '.*'; else if (c === '?') re += '.';
    else if ('.+^$()[]{}|\\'.includes(c)) re += '\\' + c; else re += c;
  }
  return new RegExp('^' + re + '$', 'i');
}

const field = {
  height: 34, boxSizing: 'border-box', padding: '0 9px', fontSize: 12.5, lineHeight: '32px',
  border: '1px solid var(--v2-border-strong)', borderRadius: 6, background: 'var(--v2-bg-surface)',
  color: 'var(--v2-text-primary)', outline: 'none', fontFamily: 'inherit',
};

export default IntentMatcherEditor;
